import React, { useState, useRef, useEffect } from 'react';
import api, { authService } from '../services/api';
import { memberService } from '../services/api';
import { socketService } from '../services/socketService';
import Avatar from './Avatar';
import imageCompression from 'browser-image-compression';
import './UserSettings.css';

export default function UserSettings({ user, onUpdate }) {
  const [name, setName] = useState(user.name);
  const [email, setEmail] = useState(user.email);
  const [subscribedToEmails, setSubscribedToEmails] = useState(Boolean(user.subscribedToEmails));
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [success, setSuccess] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(user.avatar || null);
  const [avatarFile, setAvatarFile] = useState(null);
  const [deleteAccountMessage, setDeleteAccountMessage] = useState('');
  const [deleteAccountMessageType, setDeleteAccountMessageType] = useState('');
  const fileInputRef = useRef(null);

  // Listen for real-time user profile updates
  useEffect(() => {
    const unsubscribeProfileUpdate = socketService.onUserProfileUpdated((data) => {
      if (data.user.id === user.id) {
        console.log('Received profile update for current user:', data);
        
        // Update form fields with new data
        setName(data.user.name);
        setEmail(data.user.email);
        setAvatarPreview(data.user.avatar || null);
        
        // Clear any pending avatar changes
        setAvatarFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        
        // Update parent component
        if (onUpdate) {
          onUpdate(data.user);
        }
      }
    });
    
    return () => {
      unsubscribeProfileUpdate();
    };
  }, [user.id, onUpdate]);

  // Update local state when user prop changes
  useEffect(() => {
    setName(user.name);
    setEmail(user.email);
    setSubscribedToEmails(Boolean(user.subscribedToEmails));
    setAvatarPreview(user.avatar || null);
  }, [user]);

  const handleAvatarChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setMessage('Please select an image file.');
      setSuccess(false);
      return;
    }

    setMessage('Compressing image...');
    setSuccess(null);

    try {
      // Compression options
      const options = {
        maxSizeMB: 1, // Maximum file size in MB
        maxWidthOrHeight: 512, // Maximum width or height
        useWebWorker: true,
        fileType: 'image/jpeg', // Convert to JPEG for better compression
        quality: 0.8 // Quality between 0.1 and 1
      };

      // Compress the image
      const compressedFile = await imageCompression(file, options);
      
      // Convert to base64
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64Data = e.target.result;
        setAvatarPreview(base64Data);
        setAvatarFile(base64Data);
        setMessage('');
      };
      reader.readAsDataURL(compressedFile);
    } catch (error) {
      console.error('Error compressing image:', error);
      setMessage('Failed to compress image. Please try a different image.');
      setSuccess(false);
    }
  };

  const removeAvatar = () => {
    setAvatarPreview(null);
    setAvatarFile(''); // Empty string means remove avatar
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');
    setSuccess(null);
    try {
      const updatedUser = { ...user };
      let hasChanges = false;
      const nameChanged = name.trim() !== user.name;
      const emailChanged = email.trim() !== user.email;
      const avatarChanged = avatarFile !== null;
      const subscribedChanged = subscribedToEmails !== (user.subscribedToEmails || false);

      // Update avatar first if changed
      if (avatarChanged) {
        await memberService.update('update', user.id, 'avatar', avatarFile);
        updatedUser.avatar = avatarFile || null;
        hasChanges = true;
      }

      // If both email and name are changing, update email first
      if (emailChanged) {
        await memberService.update('update', user.id, 'email', email.trim());
        updatedUser.email = email.trim();
        hasChanges = true;
      }

      // Then update name (this will send notification to new email if email was changed)
      if (nameChanged) {
        await memberService.update('update', user.id, 'name', name.trim());
        updatedUser.name = name.trim();
        hasChanges = true;
      }

      // Update email subscription
      if (subscribedChanged) {
        await memberService.update('update', user.id, 'subscribedToEmails', subscribedToEmails);
        updatedUser.subscribedToEmails = subscribedToEmails;
        hasChanges = true;
      }

      if (hasChanges) {
        let message = 'Profile updated successfully.';
        setMessage(message);
        setSuccess(true);
        setAvatarFile(null); // Reset avatar file state
        // Fetch latest user data from server to ensure UI reflects persisted value
        try {
          const res = await api.get(`/users/${updatedUser.email}`);
          if (res.data && res.data.user) {
            if (onUpdate) {
              onUpdate(res.data.user);
            }
          }
        } catch (fetchErr) {
          // fallback to local update if fetch fails
          if (onUpdate) {
            onUpdate(updatedUser);
          }
        }
      } else {
        setMessage('No changes were made.');
        setSuccess(true);
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to update profile.';
      setMessage(msg);
      setSuccess(false);
    }
    setLoading(false);
  };

  const handleDeleteAccount = async () => {
    if (!window.confirm('Are you sure you want to delete your account? This action cannot be undone and will remove all your future bookings.')) {
      return;
    }
    
    setDeleteAccountMessage('');
    setDeleteAccountMessageType('');
    setLoading(true);
    
    try {
      await memberService.update('remove', user.id);
      setDeleteAccountMessage('Account deleted successfully. Logging out...');
      setDeleteAccountMessageType('success');
      
      // Since the user account is now deleted, we can't call the logout API
      // Just clear local storage and redirect
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      
      // Small delay to show the success message before redirect
      setTimeout(() => {
        window.location.href = '/';
      }, 1000);
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to delete account.';
      setDeleteAccountMessage(msg);
      setDeleteAccountMessageType('error');
    }
    
    setLoading(false);
  };

  return (
    <div className="settings-container">
      <h2>User Settings</h2>
      <form className="settings-form" onSubmit={handleSubmit}>
        <div className="avatar-section">
          <label>Profile Picture</label>
          <div className="avatar-upload-container">
            <div className="avatar-preview">
              <Avatar 
                user={{ ...user, avatar: avatarPreview }} 
                size={64} 
                className="settings-avatar" 
              />
            </div>
            <div className="avatar-controls">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleAvatarChange}
                style={{ display: 'none' }}
                id="avatar-upload"
              />
              <label htmlFor="avatar-upload" className="avatar-upload-btn">
                Choose Image
              </label>
              {(avatarPreview || user.avatar) && (
                <button 
                  type="button" 
                  onClick={removeAvatar}
                  className="avatar-remove-btn"
                >
                  Remove
                </button>
              )}
            </div>
          </div>
          <div className="avatar-info">
            <small>Upload a profile picture.</small>
          </div>
        </div>
        
        <label>
          Sailing Number
          <input
            type="text"
            value={user.sailingNumber}
            disabled
            style={{ backgroundColor: '#f5f5f5', color: '#666' }}
          />
        </label>
        <label>
          Name
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            required
          />
        </label>
        <label>
          Email
          <input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
          />
        </label>
        <label className="email-subscription-label">
          <input
            type="checkbox"
            checked={subscribedToEmails}
            onChange={e => setSubscribedToEmails(e.target.checked)}
          />
          Subscribe to email notifications
        </label>
        <button type="submit" disabled={loading}>{loading ? 'Saving...' : 'Save Changes'}</button>
        {message && (
          <div className={`settings-message ${success ? 'success' : 'error'}`}>
            {message}
          </div>
        )}
      </form>
      
      {/* Delete Account Section */}
      <div className="delete-account-section">
        <h3>Delete Account</h3>
        <div className="delete-account-warning">
          <strong>Warning:</strong> This action is permanent and cannot be undone. 
          Deleting your account will remove all your personal information and cancel all your future bookings.
        </div>
        <button 
          type="button" 
          onClick={handleDeleteAccount}
          disabled={loading}
          className="delete-account-btn"
        >
          {loading ? 'Deleting...' : 'Delete My Account'}
        </button>
        {deleteAccountMessage && (
          <div className={`delete-account-message ${deleteAccountMessageType}`}>
            {deleteAccountMessage}
          </div>
        )}
      </div>
    </div>
  );
}
